import React, { Component, PropTypes } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import { IconButton } from 'material-ui';
import moment from 'moment';
import R from 'ramda';
import { connect } from 'react-redux';

const propTypes = {
	numberOfMonths: PropTypes.number,
	onConfirmClick: PropTypes.func,
	deleteSelectedRange: PropTypes.func,
	disabledRanges: PropTypes.array,
	selectedRanges: PropTypes.array,
	config: PropTypes.object,
	initialMonth: PropTypes.any,
	startDate: PropTypes.any,
	endDate: PropTypes.any,
	removeADateRange: PropTypes.func,
	noShowSelected: PropTypes.bool,
	renderDay: PropTypes.func,
};
class DateRangePicker extends Component {
  constructor(props) {
    super(props);
    moment.locale(props.config.locale);
    this.state = {
      currentRange: {
        from: undefined,
      },
      currentInitialMonth: props.initialMonth || this._getDateFromUTC(props.startDate),
    };
  }
  componentWillReceiveProps(nextProps) {
    if (this.props.initialMonth !== nextProps.initialMonth) {
      this.setState({ currentInitialMonth: nextProps.initialMonth });
    }
    // THis is a very messy solution. Because external components also want to control the internal state
    // I don't have time to move the external text fields into this component, so... will do if it starts
    // to cause issue
    if (nextProps.selectedRanges.length
      && !(
        moment(R.last(nextProps.selectedRanges).from).isSame(this.state.currentRange.from)
        && 
        moment(R.last(nextProps.selectedRanges).to).isSame(this.state.currentRange.to)
      )
    ) {
      this.setState({
        currentRange: R.last(nextProps.selectedRanges).to ? {} : R.last(nextProps.selectedRanges),
      });
    }
  }
  shouldComponentUpdate(nextProps, nextState) {
    const propKeys = R.keys(propTypes).filter(key => propTypes[key] !== PropTypes.func);
    return !R.equals(R.pick(propKeys, this.props), R.pick(propKeys, nextProps)) || !R.equals(this.state, nextState);
  }
  componentWillUnmount() {
    this.state = {};
  }

  _handleClick = day => {
    const { from } = this.state.currentRange;
    if (from && moment(day).isBefore(from))
      window.alert('Please select a date after the first day.');
      // we are adding a date range object to the array after user selected the from date
    else if (this.props.selectedRanges.some(range => range.to && this._isDayInRange(day, range, true))) return;
    else if (this._checkIfDisabled(day)) {
      window.alert('This day is not available.');
    } else if (from && day >= from) {
      if (this._checkIfIncludeDisabled(from, day))
        window.alert('The range you want to select includes disabled dates.');
      else {
        this.setState(
          {
            currentRange: {
              from: undefined,
            },
          },
          () =>
            this.props.onConfirmClick(this._convertSelectedRangeToUTCWhenSend({ from, to: day })),
        );
      }
    } else {
      const currentRange = DateUtils.addDayToRange(day, this.state.currentRange);
      this.setState(
        {
          currentRange,
        },
          () =>
            this.props.onConfirmClick(this._convertSelectedRangeToUTCWhenSend(currentRange)),
        );
    }
  };

  _handleMonthChange = month => {
    const year = month.getFullYear();
    const currentMonth = month.getMonth();
    const day = month.getDate();
    const adjustment = this.props.numberOfMonths - 1;
    this.setState({
      currentInitialMonth: new Date(
        year,
        moment(month).isAfter(this.state.currentInitialMonth)
          ? currentMonth + adjustment
          : currentMonth - adjustment,
        day,
      ),
    });
  };

  handleResetClick = e => {
    e.preventDefault();
    this.setState({
      currentRange: {
        from: undefined,
      },
    });
  };

  _handleRemove = index => this.props.removeADateRange(index);

  // use this function to reset date to the same time
  _getDateFromUTC = date => {
    const dateObject = new Date(date);
    return new Date(dateObject.getUTCFullYear(), dateObject.getUTCMonth(), dateObject.getUTCDate());
  };

  _getUTCDay = day =>
    day ? new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate())) : day;

  _convertSelectedRangeToUTCWhenSend = range => ({
    from: range.from ? this._getUTCDay(range.from) : range.from,
    to: range.to ? this._getUTCDay(range.to) : range.to,
  });

  _isDayInRange = (day, range, inclusive) => {
    if (range.from && range.to)
      return moment(day).isBetween(range.from, range.to, 'day', inclusive ? '[]' : '()');
    if (range.from) return moment(day).isSame(this._getDateFromUTC(range.from), 'day');
    return false;
  };

  _checkIfDisabled = day => {
    const { disabledRanges, startDate, endDate } = this.props;
    const outsideRange =
      moment(day).isBefore(this._getDateFromUTC(startDate) || new Date('1000-01-31'), 'day') ||
      moment(day).isAfter(this._getDateFromUTC(endDate) || new Date('11000-01-31'), 'day');
    if (!disabledRanges) return outsideRange;
    return (
      disabledRanges.some(range => {
        if (range.from && range.to) return this._isDayInRange(day, range, true);
        return moment(day).isSame(range, 'day');
      }) || outsideRange
    );
  };

  _checkIfIncludeDisabled = (from, to) => {
    return (this.props.disabledRanges || []).some(range => range.from > from && range.to < to);
  };

  render() {
    const { numberOfMonths, noShowSelected, selectedRanges } = this.props;
    const { currentRange, currentInitialMonth } = this.state;
    const selectedFunc = (() => {
      if (!selectedRanges.length) return day => this._isDayInRange(day, currentRange, true);
      return day =>
        selectedRanges.some(selectedRange => this._isDayInRange(day, selectedRange, true));
    })();
    const modifiers = {
      disabled: day => this._checkIfDisabled(day),
      selected: selectedFunc,
    };
    return (
      <div
        style={
          noShowSelected
            ? { display: 'flex', flexDirection: 'column-reverse' }
            : { display: 'flex', alignItems: 'flex-start' }
        }
      >
        <div style={{ maxWidth: '550px', marginTop: noShowSelected ? '0' : '12px' }}>
          <DayPicker
            initialMonth={currentInitialMonth}
            month={currentInitialMonth}
            onMonthChange={this._handleMonthChange}
            numberOfMonths={numberOfMonths}
            modifiers={modifiers}
            renderDay={this.props.renderDay}
            onDayClick={this._handleClick}
          />
        </div>
        {!noShowSelected ? (
          <div style={{ paddingTop: '12px', width: '180px', marginLeft: '16px' }}>
            <div style={{ fontSize: '12px', visibility: currentRange.from ? 'visible' : 'hidden' }}>
              From {moment(currentRange.from).format('D MMM YYYY')}
              <a
                href="#"
                style={{ color: '#ff4081', display: 'inline-block', marginLeft: '6px' }}
                onClick={this.handleResetClick}
              >
                Reset
              </a>
            </div>
            {!currentRange.from && (
              <div style={{ fontSize: '12px' }}>
                Please select the <strong>first day</strong>.
              </div>
            )}
            {currentRange.from && (
              <div style={{ fontSize: '12px' }}>
                Please select the <strong>last day</strong>.
              </div>
            )}
            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
              {selectedRanges && selectedRanges.length ? (
                selectedRanges.map((selectedRange, index) => {
                  const { from, to } = selectedRange;
                  return (
                    <div
                      key={index}
                      className="flex-tc-sb"
                      style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.87)' }}
                    >
                      {moment(from).isSame(to, 'day')
                        ? moment(from).format('D MMM YYYY')
                        : `${moment(from).format('D MMM YYYY')} - ${to ? moment(to).format(
                            'D MMM YYYY',
                          ) : ''}`}
                      <IconButton
                        iconClassName="tc-icon-delete2"
                        iconStyle={{ color: '#ff4081', top: '4px', fontSize: '20px' }}
                        style={{ padding: '0', height: '24px', width: '24px' }}
                        onClick={() => this._handleRemove(index)}
                      />
                    </div>
                  );
                })
              ) : (
                <span />
              )}
            </div>
          </div>
        ) : (
          <div style={{ width: '380px', display: 'flex' }}>
            <div style={{ fontSize: '12px', visibility: currentRange.from ? 'visible' : 'hidden' }}>
              From {moment(currentRange.from).format('D MMM YYYY')}
              <a
                href="#"
                style={{ color: '#ff4081', display: 'inline-block', marginLeft: '6px' }}
                onClick={this.handleResetClick}
              >
                Reset
              </a>
            </div>
            {!currentRange.from && (
              <div style={{ fontSize: '12px' }}>
                Please select the <strong>first day</strong>.
              </div>
            )}
            {currentRange.from && (
              <div style={{ fontSize: '12px', marginLeft: '16px' }}>
                Please select the <strong>last day</strong>.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

DateRangePicker.propTypes = propTypes;

const mapStateToProps = ({ config }) => {
  return {
    config: config.toJS(),
  };
};

export default connect(mapStateToProps)(DateRangePicker);



// WEBPACK FOOTER //
// ./src/app/components/tc-ui/date-range-picker.jsx